import React from "react";
import "./EmbeddingRootApp.css";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useState, useEffect } from "react";
import { bindActionCreators } from "redux";
import { Box, Button, CircularProgress } from "@mui/material";

import { onLogout, onLogin } from "../../redux/session/actions";
import BasicToolbar from "../../generic/components/toolbar/BasicToolbar";
import { embeddingService } from "../../generic/services/EmbeddingService";
import CustomTabs from "../../generic/components/main-content/CustomTabs";
import { promptService } from "../../generic/services/PromptService";
import { dossierService } from "../../generic/services/DossierService";
import DynamicAlert from "../../generic/components/error-display/DynamicAlert";
import { FilterPanel } from "../../generic/components/filters/FilterPanel";
import { mstrPath } from "../../utils/mstrBase";

const PROMPTED = 2;
const CASHFLOW_BASE_URL =
  "http://intra.teic.techint.net/sites/reporting/DIAFI/Economics/CashFlow/CashFlow_";
const COMPACT_DOSSIER_TABS = ["pl"];
const MONTH_CODES = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DEC"];

const getCurrentDateContext = () => {
  const now = new Date();

  return {
    currentYear: now.getFullYear(),
    currentMonthNumber: now.getMonth() + 1,
    currentMonthCode: MONTH_CODES[now.getMonth()],
  };
};

const sanitizeDateFilters = (rawFilters, options = {}) => {
  if (!rawFilters) return rawFilters;

  const { defaultCurrentYear = false } = options;
  const { currentYear, currentMonthNumber, currentMonthCode } = getCurrentDateContext();
  const filters = {};

  Object.entries(rawFilters).forEach(([key, value]) => {
    filters[key] = {
      ...value,
      elements: Array.isArray(value?.elements) ? [...value.elements] : value?.elements,
      displayedElements: Array.isArray(value?.displayedElements)
        ? [...value.displayedElements]
        : value?.displayedElements,
    };
  });

  const yearFilter = filters.Year;
  const monthFilter = filters.Month;

  if (yearFilter) {
    if (Array.isArray(yearFilter.elements)) {
      yearFilter.elements = yearFilter.elements.filter((ele) => Number(ele?.id) <= currentYear);
    }
    if (Array.isArray(yearFilter.displayedElements)) {
      yearFilter.displayedElements = yearFilter.displayedElements.filter(
        (ele) => Number(ele?.id) <= currentYear,
      );
    }

    if (defaultCurrentYear) {
      const hasCurrentYear = yearFilter.elements?.some((ele) => Number(ele?.id) === currentYear);

      if (hasCurrentYear) {
        yearFilter.value = String(currentYear);
      }
    }

    const selectedYearExists = yearFilter.elements?.some((ele) => String(ele?.id) === String(yearFilter.value));

    if (!selectedYearExists) {
      yearFilter.value = String(currentYear);
    }
  }

  if (monthFilter) {
    const selectedYear = Number(yearFilter?.value);
    const targetKey = monthFilter.displayedElements ? "displayedElements" : "elements";
    const monthElements = Array.isArray(monthFilter[targetKey]) ? [...monthFilter[targetKey]] : [];

    if (selectedYear === currentYear) {
      const allowedMonths = monthElements.filter((ele) => Number(ele?.id_int || 0) <= currentMonthNumber);
      const monthValueStillAvailable = allowedMonths.some((ele) => ele?.id === monthFilter.value);

      if (!monthValueStillAvailable) {
        const fallbackMonth =
          allowedMonths.find((ele) => ele?.id === currentMonthCode) || allowedMonths[allowedMonths.length - 1];

        monthFilter.value = fallbackMonth?.id || "";
      }
    }
  }

  return filters;
};

const ensureRegionCountryConsistency = (rawFilters, hiddenFilterValues) => {
  const filters = { ...rawFilters };

  if (!filters?.Country) {
    return filters;
  }

  const adaptedFilters = promptService.adaptPrompts(filters, hiddenFilterValues);
  const adaptedCountry = adaptedFilters?.Country;

  if (!adaptedCountry) {
    return filters;
  }

  const availableCountries = Array.isArray(adaptedCountry.displayedElements)
    ? adaptedCountry.displayedElements
    : Array.isArray(adaptedCountry.elements)
      ? adaptedCountry.elements
      : [];

  const currentCountryValue = filters.Country?.value;
  const countryIsStillValid = availableCountries.some((element) => element?.id === currentCountryValue);

  if (countryIsStillValid) {
    return filters;
  }

  const totalCountry = availableCountries.find(
    (element) =>
      String(element?.id || "")
        .trim()
        .toUpperCase() === "TOTAL" ||
      String(element?.label || "")
        .trim()
        .toUpperCase()
        .startsWith("TOD"),
  );

  const nextCountryValue = totalCountry?.id || availableCountries[0]?.id || "";

  filters.Country = {
    ...filters.Country,
    value: nextCountryValue,
  };

  return filters;
};

function EmbeddingRootApp(props) {
  const { token, config, messageBundles, groups } = props;
  const history = useHistory();
  const getTabId = (doss) => doss?.id || doss?.key;
  const normalizeTabName = (doss) => String(doss?.tabName || doss?.key || "").trim().toLowerCase();
  const normalizeCompactTabName = (doss) => normalizeTabName(doss).replace(/\s+/g, "");
  const isPlHistoryTab = (doss) => {
    const normalizedName = normalizeCompactTabName(doss);

    return normalizedName.includes("pl") && normalizedName.includes("history");
  };
  const isGrossMgTab = (doss) => normalizeCompactTabName(doss).includes("grossmg");
  const isRevenueTab = (doss) => normalizeTabName(doss).includes("revenue");
  const isCashflowTab = (doss) => normalizeTabName(doss).includes("cashflow");
  const initialDossier = config.applications.embedding.embeddedDossier[0];
  //Dossier rendered - this is the embedding API instance.
  const [localDossier, setLocalDossier] = useState(null);
  //Instance, prompts and details for the rendered dossier
  const [dossierData, setDossierData] = useState(null);
  // Pre-warmed API instances (steps 1-3) for background dossiers
  const [preWarmedInstances, setPreWarmedInstances] = useState({});
  // Incremented after each filter change to re-trigger background pre-warming
  const [preWarmTrigger, setPreWarmTrigger] = useState(0);
  const [error, setError] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  //Id of the current dashboard
  const [currentDash, setCurrentDash] = useState(getTabId(initialDossier));
  const allFilters = sanitizeDateFilters(
    promptService.getPromptsForDashboard(
      currentDash,
      config.applications.embedding,
      config.filterConfig.filters,
    ),
    { defaultCurrentYear: true },
  );
  //Filters to be rendered and their elements
  const [filters, setFilters] = useState(allFilters);
  //Hidden filter values due to user privileges or groups.
  const hiddenFilterValues = promptService.getHiddenFilters(
    groups,
    config.filterConfig.restrictions,
  );

  useEffect(() => {
    if (!token) {
      history.push(mstrPath("login"));
    } else {
      if (!isCashflowTab(initialDossier)) {
        renderDossier(initialDossier, true);
      }
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    const otherDossiers = config.applications.embedding.embeddedDossier.filter(
      (doss) => getTabId(doss) !== getTabId(initialDossier) && !isCashflowTab(doss),
    );

    const preWarmDossier = async (doss) => {
      try {
        let instance = null;
        let prompts = null;

        try {
          instance = await dossierService.createDossierInstance(
            config,
            token,
            doss.projectId,
            doss.id,
          );
        } catch (e) {
          return;
        }

        if (instance != null && instance.status === PROMPTED) {
          prompts = await promptService.populate(
            config,
            token,
            doss.projectId,
            doss.id,
            instance.mid,
          );

          const dossierFilters = sanitizeDateFilters(
            promptService.getPromptsForDashboard(
              getTabId(doss),
              config.applications.embedding,
              config.filterConfig.filters,
            ),
            { defaultCurrentYear: true },
          );

          await promptService.answerv2(
            config,
            token,
            doss.projectId,
            doss.id,
            instance.mid,
            prompts,
            Object.values(dossierFilters),
          );
        }

        setPreWarmedInstances((prev) => ({
          ...prev,
          [getTabId(doss)]: { instance, prompts },
        }));
      } catch (e) {
        // Silent fail — falls back to normal rendering on tab click
      }
    };

    Promise.all(otherDossiers.map(preWarmDossier));
  }, [token, preWarmTrigger]);

  const renderDossier = async (doss, isFirstRender) => {
    setIsRendering(true);
    const tabId = getTabId(doss);
    const preWarmed = preWarmedInstances[tabId];

    try {
      let prompts = null,
        instance = null;

      if (preWarmed) {
        // Use pre-warmed instance — skip API steps 1-3
        instance = preWarmed.instance;
        prompts = preWarmed.prompts;
      } else {
        try {
          //Create instance of the dossier
          instance = await dossierService.createDossierInstance(
            config,
            token,
            doss.projectId,
            doss.id,
          );
        } catch (e) {
          if (e.id != "INVALID_CREDENTIALS") {
            console.log(messageBundles.GENERIC_13);
            console.log(e.message);
            setError({ severity: "error", message: messageBundles.GENERIC_13 });
          } else {
            throw e;
          }
        }
        if (instance != null && instance.status === PROMPTED) {
          //If the dossier is prompted and the instance was generated.
          //Populate prompts with all elements and answers
          prompts = await promptService.populate(
            config,
            token,
            doss.projectId,
            doss.id,
            instance.mid,
          );
          //Answer prompts
          await promptService.answerv2(
            config,
            token,
            doss.projectId,
            doss.id,
            instance.mid,
            prompts,
            Object.values(filters),
          );
          if (isFirstRender) {
            //set filters with adapted elements due to already selected elements (in this case, the default ones)
            setFilters(promptService.adaptPrompts(filters, hiddenFilterValues));
          }
        }
      }
      //save the dossier data - project id and dossier instance
      setDossierData({
        projectId: doss.projectId,
        dossierId: doss.id,
        dossierInstanceId: instance,
        prompts: prompts,
      });
      //Render dashboard through embedding
      const dossierEmbedded = await embeddingService.renderDossier(
        document.getElementById("embedding-container_" + doss.id),
        config.samlLoginURL,
        config.backendServiceURL + "app/",
        doss.projectId,
        doss.id,
        localDossier,
        token,
        instance,
        "consumption",
      );

      //Save the dossier embedded - instance returned by the embedding API.
      //This is so we can render the dashboards on tab change without rerendering.
      setLocalDossier({ ...localDossier, [doss.id]: dossierEmbedded });
    } catch (e) {
      if (e.id === "INVALID_CREDENTIALS") {
        //If the session dies in Strategy, go back to login screen.
        onLogout();
        history.push(mstrPath("login"));
      } else {
        setError(e.message);
      }
    } finally {
      setIsRendering(false);
    }
  };
  const handleFilterChange = async (filterValues) => {
    setError(null);
    setIsRendering(true);
    // Invalidate pre-warmed instances — filters changed so cached data is stale
    setPreWarmedInstances({});
    let instance = null;
    const normalizedFilters = sanitizeDateFilters(filterValues);
    const normalizedConsistentFilters = ensureRegionCountryConsistency(
      normalizedFilters,
      hiddenFilterValues,
    );

    try {
      //Set the dashboard for reprompt.
      instance = await promptService.rePrompt(
        config,
        token,
        dossierData.projectId,
        dossierData.dossierId,
        dossierData.dossierInstanceId.mid,
      );
      //Answer prompts with the new selected elements
      await promptService.answerv2(
        config,
        token,
        dossierData.projectId,
        dossierData.dossierId,
        instance.mid,
        dossierData.prompts,
        Object.values(normalizedConsistentFilters),
      );
      //Save the instance and new prompt answers
      setDossierData({
        projectId: dossierData.projectId,
        dossierId: dossierData.dossierId,
        dossierInstanceId: {
          ...dossierData.dossierInstanceId,
          mid: instance?.mid,
        },
        prompts: dossierData.prompts,
      });
      //Render the dashboard after filter changes
      const dossierEmbedded = await embeddingService.renderDossier(
        document.getElementById("embedding-container_" + dossierData.dossierId),
        config.samlLoginURL,
        config.backendServiceURL + "app/",
        dossierData.projectId,
        dossierData.dossierId,
        localDossier,
        token,
        { ...dossierData.dossierInstanceId, mid: instance?.mid },
        "consumption",
      );

      //Save the dossier embedded - instance returned by the embedding API.
      //This is so we can render the dashboards on tab change without rerendering.
      // Only keep the current dossier — others are stale after filter change
      setLocalDossier({ [dossierData.dossierId]: dossierEmbedded });
      //Save filters adapted.
      setFilters(promptService.adaptPrompts(normalizedConsistentFilters, hiddenFilterValues));
      // Re-trigger background pre-warming with new filter values
      setPreWarmTrigger((prev) => prev + 1);
    } catch (e) {
      setError({ severity: "error", message: messageBundles.GENERIC_14 });
    } finally {
      setIsRendering(false);
    }
  };
  const getCurrentPeriod = () => {
    const year = filters?.Year?.value || "";
    const monthSelected = filters?.Month?.value;
    const monthIdInt =
      filters?.Month?.elements?.find((month) => month.id == monthSelected)?.id_int || "";

    return `${year}${monthIdInt}`;
  };

  const openCashflow = () => {
    const period = getCurrentPeriod();

    window.open(`${CASHFLOW_BASE_URL}${period}.pptx?web=1`, "_blank", "noopener,noreferrer");
  };

  const onChangeTab = (dossierID) => {
    const tabDossier = config.applications.embedding.embeddedDossier.find(
      (x) => getTabId(x) === dossierID,
    );

    //Set current dashboard with dossier ID
    setCurrentDash(dossierID);
    if (!tabDossier || isCashflowTab(tabDossier)) return;

    if (localDossier?.[dossierID] == undefined) {
      //Render the new dashboard on tab change.
      renderDossier(tabDossier, false);
    }
  };

  const selectedTab = config.applications.embedding.embeddedDossier.find(
    (doss) => getTabId(doss) === currentDash,
  );
  const showCashflowOnly = isCashflowTab(selectedTab);

  return (
    <Box id="embedding-root">
      <BasicToolbar id="embedding-toolbar" pageName="Home / Economics / MSTR">
        <FilterPanel
          config={config}
          currentDash={currentDash}
          filters={filters}
          handleFilterChange={handleFilterChange}
          hiddenFilterValues={hiddenFilterValues}
          setFilters={setFilters}
        />
      </BasicToolbar>
      <CustomTabs onTabChange={onChangeTab}>
        {config.applications.embedding.embeddedDossier.map((doss) => {
          return <Box key={doss.key} did={getTabId(doss)} tabName={doss.tabName} />;
        })}
      </CustomTabs>
      {showCashflowOnly && (
        <Box className="cashflow-only-container">
          <Button className="cashflow-open-button" variant="contained" onClick={openCashflow}>
            OPEN DOCUMENT IN A ANOTHER WINDOW
          </Button>
        </Box>
      )}
      {!showCashflowOnly && (
        <Box className="mstr-dossier-area">
          {isRendering && (
            <Box className="mstr-loading-overlay">
              <CircularProgress size={36} thickness={4} />
            </Box>
          )}
          {config.applications.embedding.embeddedDossier
            .filter((doss) => !isCashflowTab(doss))
            .map((doss) => {
              const tabId = getTabId(doss);
              const display = currentDash == tabId ? "block" : "none";
              const isCompactTab = COMPACT_DOSSIER_TABS.includes(normalizeTabName(doss));
              const isPlHistory = isPlHistoryTab(doss);
              const isGrossMg = isGrossMgTab(doss);
              const isRevenue = isRevenueTab(doss);
              const normalizedContainerClassName = `mstr-dossier-container${isCompactTab ? " mstr-dossier-container-compact" : ""}${isPlHistory ? " mstr-dossier-container-pl-history" : ""}${isRevenue ? " mstr-dossier-container-revenue" : ""}${isGrossMg ? " mstr-dossier-container-grossmg" : ""}`;

              return (
                <Box
                  className={normalizedContainerClassName}
                  key={tabId}
                  id={"embedding-container_" + doss.id}
                  sx={{
                    display,
                  }}
                />
              );
            })}
        </Box>
      )}
      {error && <DynamicAlert error={error} resetErrorBoundary={(x) => setError(null)} />}
    </Box>
  );
}
const mapStateToProps = (state) => {
  return {
    token: state.userDetails?.token,
    config: state.configuration,
    groups: state.userDetails?.groups,
  };
};
const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      onLogin: onLogin,
      onLogout: onLogout,
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(EmbeddingRootApp);
